import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, KeyRound } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import type * as S from "@releo/shared";
import { Badge, Button, Card, CardHeader, EmptyState, ErrorBox, Field, Input, PageHeader, Select, Spinner, Table, Td, Th } from "@/components/ui";
import { http } from "@/lib/api";
import { useEntitlements, useMe, useOrgMembers } from "@/lib/queries";
import { isInstanceAdmin } from "@/lib/roles";
import { formatDate } from "@/lib/utils";

/** Action prefixes offered as filters (the API filters by prefix). */
const ORG_CATEGORIES = ["build.", "release.", "app.", "keystore.", "publication.", "tag.", "organization.", "apple_credential.", "google_credential.", "llm_provider.", "git_provider.", "device.", "sso_provider.", "branding.", "billing.", "audit."] as const;
const INSTANCE_CATEGORIES = ["auth.", "user.", "license.", "settings.", "instance."] as const;

type Scope = "org" | "instance";
interface Filters {
  action: string;
  actorId: string;
  from: string;
  to: string;
}

function queryString(filters: Filters, extra: Record<string, string> = {}) {
  const params = new URLSearchParams();
  if (filters.action) params.set("action", filters.action);
  if (filters.actorId) params.set("actorId", filters.actorId);
  if (filters.from) params.set("from", new Date(`${filters.from}T00:00:00`).toISOString());
  if (filters.to) params.set("to", new Date(`${filters.to}T23:59:59.999`).toISOString());
  for (const [k, v] of Object.entries(extra)) params.set(k, v);
  const s = params.toString();
  return s ? `?${s}` : "";
}

/** Human label of an action, falling back to its code. */
export function useActionLabel() {
  const { t } = useTranslation();
  return (action: string) => t(`audit.actions.${action.replace(/\./g, "_")}`, { defaultValue: action });
}

function Actor({ entry }: { entry: S.AuditEntry }) {
  const { t } = useTranslation();
  if (entry.apiKey) {
    return (
      <span className="flex items-center gap-1.5" title={entry.actor?.email}>
        <KeyRound className="h-3.5 w-3.5 text-fg-muted" />
        <span>
          {entry.apiKey.name} <span className="text-xs text-fg-muted">({entry.actor?.name ?? t("audit.deletedUser")})</span>
        </span>
      </span>
    );
  }
  if (entry.actor) {
    return (
      <span title={entry.actor.email}>
        {entry.actor.name}
        <span className="block text-xs text-fg-muted">{entry.actor.email}</span>
      </span>
    );
  }
  const attempted = typeof entry.meta?.email === "string" ? entry.meta.email : null;
  return <span className="text-fg-muted">{attempted ?? t("audit.system")}</span>;
}

function AuditTable({ scope }: { scope: Scope }) {
  const { t, i18n } = useTranslation();
  const label = useActionLabel();
  const members = useOrgMembers(scope === "org");
  const [filters, setFilters] = useState<Filters>({ action: "", actorId: "", from: "", to: "" });
  const [open, setOpen] = useState<string | null>(null);
  const base = scope === "org" ? "/org/audit-log" : "/admin/audit-log";
  const log = useInfiniteQuery({
    queryKey: ["audit", scope, filters],
    queryFn: ({ pageParam }) => http.get<S.AuditLogPage>(`${base}${queryString(filters, pageParam ? { cursor: pageParam } : {})}`),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const first = log.data?.pages[0];
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];
  const categories = scope === "org" ? ORG_CATEGORIES : INSTANCE_CATEGORIES;
  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <Card>
      <CardHeader
        title={scope === "org" ? t("audit.orgTitle") : t("audit.instanceTitle")}
        description={first?.retentionDays ? t("audit.retention", { count: first.retentionDays }) : undefined}
        actions={
          first?.canExport && (
            <a href={`/api/v1${base}/export${queryString(filters)}`} download>
              <Button variant="outline" size="sm">
                <Download className="h-3.5 w-3.5" />
                {t("audit.export")}
              </Button>
            </a>
          )
        }
      />
      <div className="grid gap-3 border-b p-5 sm:grid-cols-4">
        <Field label={t("audit.category")} htmlFor={`audit-category-${scope}`}>
          <Select id={`audit-category-${scope}`} value={filters.action} onChange={(e) => set({ action: e.target.value })}>
            <option value="">{t("audit.allActions")}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {t(`audit.categories.${c.slice(0, -1)}`)}
              </option>
            ))}
          </Select>
        </Field>
        {scope === "org" && (
          <Field label={t("audit.actor")} htmlFor="audit-actor">
            <Select id="audit-actor" value={filters.actorId} onChange={(e) => set({ actorId: e.target.value })}>
              <option value="">{t("audit.everyone")}</option>
              {members.data?.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t("audit.from")} htmlFor={`audit-from-${scope}`}>
          <Input id={`audit-from-${scope}`} type="date" value={filters.from} onChange={(e) => set({ from: e.target.value })} />
        </Field>
        <Field label={t("audit.to")} htmlFor={`audit-to-${scope}`}>
          <Input id={`audit-to-${scope}`} type="date" value={filters.to} onChange={(e) => set({ to: e.target.value })} />
        </Field>
      </div>
      {log.isLoading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : log.error ? (
        <div className="p-5">
          <ErrorBox error={log.error} />
        </div>
      ) : !entries.length ? (
        <div className="p-5">
          <EmptyState title={t("audit.empty")} />
        </div>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th className="w-6" />
                <Th>{t("audit.date")}</Th>
                <Th>{t("audit.actor")}</Th>
                <Th>{t("audit.action")}</Th>
                <Th className="hidden md:table-cell">{t("audit.target")}</Th>
                <Th className="hidden md:table-cell">IP</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <Fragment key={e.id}>
                  <tr className="cursor-pointer hover:bg-muted/50" onClick={() => setOpen(open === e.id ? null : e.id)} aria-expanded={open === e.id}>
                    <Td>{open === e.id ? <ChevronDown className="h-4 w-4 text-fg-muted" /> : <ChevronRight className="h-4 w-4 text-fg-muted" />}</Td>
                    <Td className="whitespace-nowrap text-fg-muted">{formatDate(e.createdAt, i18n.language)}</Td>
                    <Td>
                      <Actor entry={e} />
                    </Td>
                    <Td>
                      <span className="font-medium">{label(e.action)}</span>
                      <span className="block font-mono text-xs text-fg-muted">{e.action}</span>
                    </Td>
                    <Td className="hidden md:table-cell">
                      {e.targetType && (
                        <span className="text-xs">
                          <Badge>{e.targetType}</Badge> <span className="font-mono text-fg-muted">{e.targetId?.slice(0, 8)}</span>
                        </span>
                      )}
                    </Td>
                    <Td className="hidden font-mono text-xs text-fg-muted md:table-cell">{e.ip}</Td>
                  </tr>
                  {open === e.id && (
                    <tr>
                      <Td />
                      <Td className="bg-muted/30" colSpan={5}>
                        <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs" aria-label={t("audit.details")}>
                          {JSON.stringify({ target: e.targetId ? { type: e.targetType, id: e.targetId } : null, details: e.meta }, null, 2)}
                        </pre>
                      </Td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </Table>
          {log.hasNextPage && (
            <div className="flex justify-center border-t p-3">
              <Button variant="ghost" size="sm" loading={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
                {t("audit.loadMore")}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export function AuditPage() {
  const { t } = useTranslation();
  const me = useMe();
  const entitlements = useEntitlements();
  const [scope, setScope] = useState<Scope>("org");
  if (!entitlements.data || !me.data) return <Spinner />;
  const cloud = entitlements.data.deployment === "cloud";
  const instanceAdmin = isInstanceAdmin(me.data);
  const entitled = entitlements.data.features.includes("audit_log");

  return (
    <>
      <PageHeader
        title={t("audit.title")}
        description={t("audit.help")}
        actions={
          instanceAdmin && (
            <div className="flex rounded-md border bg-panel p-0.5 text-sm" role="tablist">
              {(["org", "instance"] as const).map((s) => (
                <button
                  key={s}
                  role="tab"
                  aria-selected={scope === s}
                  className={scope === s ? "rounded bg-muted px-3 py-1 font-medium" : "px-3 py-1 text-fg-muted hover:text-fg"}
                  onClick={() => setScope(s)}
                >
                  {t(s === "org" ? "audit.tabOrganization" : "audit.tabInstance")}
                </button>
              ))}
            </div>
          )
        }
      />
      {scope === "instance" && (entitled || cloud) ? (
        // Keys: each view keeps its own filters.
        <AuditTable key="instance" scope="instance" />
      ) : entitled ? (
        <AuditTable key="org" scope="org" />
      ) : (
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                {t("audit.title")} <Badge tone="violet">{cloud ? "Pro" : "Enterprise"}</Badge>
              </span>
            }
            description={t(cloud ? "audit.requiresPlan" : "audit.requiresEnterprise")}
          />
        </Card>
      )}
    </>
  );
}
